/**
 * Dialog showing a DID's assignment history.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Typography,
  Paper,
} from "@mui/material";
import { didsApi } from "../../api/dids";
import type { DIDAssignment } from "../../types/did";

interface AssignmentHistoryDialogProps {
  open: boolean;
  phoneNumber: string;
  didId: string | null;
  onClose: () => void;
}

/** Assignment-history viewer. */
export default function AssignmentHistoryDialog({
  open,
  phoneNumber,
  didId,
  onClose,
}: AssignmentHistoryDialogProps) {
  const [history, setHistory] = useState<DIDAssignment[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!open || !didId) {
        return;
      }
      setLoading(true);
      setError("");
      try {
        const response = await didsApi.assignments(didId);
        setHistory(response.data);
      } catch {
        setError("Failed to load assignment history.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [open, didId]);

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "—";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assignment history — {phoneNumber}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <Typography>Loading…</Typography>}
        {!loading && !error && history.length === 0 && (
          <Typography color="text.secondary">
            No assignments recorded for this DID.
          </Typography>
        )}
        {!loading && history.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Assigned at</TableCell>
                  <TableCell>Assigned by</TableCell>
                  <TableCell>Unassigned at</TableCell>
                  <TableCell align="right">Monthly cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.customer_id}</TableCell>
                    <TableCell>{formatDate(row.assigned_at)}</TableCell>
                    <TableCell>{row.assigned_by}</TableCell>
                    <TableCell>{formatDate(row.unassigned_at)}</TableCell>
                    <TableCell align="right">
                      ${row.monthly_cost.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
